export type PortfolioProject = {
  slug: string
  name: string
  summary: string
  stack: string[]
  href?: string
  section: 'building' | 'client'
  detail: {
    eyebrow: string
    lead: string
    about: string[]
    features: { title: string; text: string }[]
    focus: string
  }
}

// Keep descriptions specific to the recorded project scope. Avoid unverified results or adoption claims.
export const portfolioProjects: PortfolioProject[] = [
  {
    "slug": "gridexplorer",
    "name": "GridExplorer",
    "summary": "Keep a map of the places you’ve explored in Singapore, with GPS check-ins, notes, and saved landmarks.",
    "href": "/login",
    "stack": [
      "React",
      "TypeScript",
      "Firebase",
      "Leaflet",
      "Tailwind"
    ],
    "section": "building",
    "detail": {
      "eyebrow": "Personal product",
      "lead": "A personal map of Singapore: where you’ve been, what you noticed, and places to return to.",
      "about": [
        "GridExplorer divides Singapore into selectable grids. Mark the places you’ve visited, add a note, and save landmarks as you go.",
        "The map is the centre of the app. GPS check-ins, visit history, and location details sit around it so you can record a trip without losing your place."
      ],
      "features": [
        {
          "title": "Track your visits",
          "text": "Mark grids as visited and add notes about what you found there."
        },
        {
          "title": "Check in with GPS",
          "text": "Use your location to confirm a visit while keeping the map still."
        },
        {
          "title": "Save landmarks",
          "text": "Keep places you want to remember alongside your exploration history."
        },
        {
          "title": "Use it on the move",
          "text": "Open stats and location details in panels designed for smaller screens."
        }
      ],
      "focus": "Keeping the map easy to use on a phone. A check-in or an open panel should help you record a visit without interrupting your exploration."
    }
  },
  {
    "slug": "ai-tuition",
    "name": "AI Tuition Management",
    "summary": "Plan tuition sessions, record what students need to practise, and turn those notes into quizzes and revision.",
    "href": "/tuition",
    "stack": [
      "React",
      "TypeScript",
      "Supabase",
      "OpenAI",
      "Deno"
    ],
    "section": "building",
    "detail": {
      "eyebrow": "Personal product",
      "lead": "One place to plan a lesson, record what needs work, and help students practise before the next session.",
      "about": [
        "Tuition involves more than the lesson itself. There are schedules to manage, resources to share, and learning points to revisit. This workspace brings those parts together for tutors and students.",
        "Tutors manage students, groups, and session records. Students use practice tools built around their learning, including flashcards, Chinese character writing, and AI-assisted quizzes."
      ],
      "features": [
        {
          "title": "Keep lessons organised",
          "text": "Manage sessions, student groups, and shared resources from a tutor workspace."
        },
        {
          "title": "Record what needs practice",
          "text": "Save learning points from each session so they can guide later revision."
        },
        {
          "title": "Review over time",
          "text": "Study flashcards with spaced repetition and practise writing Chinese characters with feedback on mistakes."
        },
        {
          "title": "Turn notes into questions",
          "text": "Generate and refine AI-assisted quizzes using the learning points you have recorded."
        }
      ],
      "focus": "Connecting lesson records to practice. The useful part is the path from noticing a gap during a session to giving the student something specific to work on."
    }
  },
  {
    "slug": "roost",
    "name": "Roost",
    "summary": "Keep AI chats, notes, and saved prompts within reach in a floating macOS window.",
    "stack": [
      "Electron",
      "JavaScript",
      "macOS"
    ],
    "section": "building",
    "detail": {
      "eyebrow": "Desktop app",
      "lead": "A small desktop home for the AI tools, notes, and prompts you use throughout the day.",
      "about": [
        "Roost lives in the macOS menu bar and opens as a floating window. It brings services such as ChatGPT, Gemini, and Claude into a workspace you can reach without searching through browser tabs.",
        "Notes and saved prompts sit alongside the chats. It also supports local models through LM Studio or Ollama."
      ],
      "features": [
        {
          "title": "Switch between services",
          "text": "Move between built-in AI services or add your own URLs, with separate sign-in sessions."
        },
        {
          "title": "Keep useful prompts",
          "text": "Save prompts in a searchable library and reuse them when you need them."
        },
        {
          "title": "Make a quick note",
          "text": "Write notes with Markdown preview while keeping your chat close by."
        },
        {
          "title": "Work from the menu bar",
          "text": "Use global shortcuts and a saved window layout to return to your workspace."
        }
      ],
      "focus": "Making small, repeated actions easier: open a chat, find a prompt, save a thought, and get back to what you were doing."
    }
  },
  {
    "slug": "teleboard",
    "name": "Teleboard",
    "summary": "Browse Telegram chats as a visual board, with media previews, filters, and local saves.",
    "stack": [
      "Electron",
      "React",
      "Python",
      "Telethon"
    ],
    "section": "building",
    "detail": {
      "eyebrow": "Desktop app",
      "lead": "A different way to browse Telegram when pictures and saved conversations matter more than the message timeline.",
      "about": [
        "Teleboard lays out Telegram chats as cards in a desktop app. It is designed for browsing conversations with lots of media and keeping useful material organised.",
        "Filters, sidebar groups, and local saves make it easier to return to something you found. Photos and videos open in a dedicated viewer."
      ],
      "features": [
        {
          "title": "Browse a visual board",
          "text": "Scan chat cards with media previews and filter the conversations you want to see."
        },
        {
          "title": "Organise your inbox",
          "text": "Group and reorder chats in the sidebar, and keep track of unread messages."
        },
        {
          "title": "Look closer at media",
          "text": "Open photos and videos in a larger viewer with zoom controls."
        },
        {
          "title": "Keep useful material",
          "text": "Save items locally and use text search to find your way back to a conversation."
        }
      ],
      "focus": "Keeping a busy board readable. The controls should help you find a conversation or inspect an image without crowding the content."
    }
  },
  {
    "slug": "inkwell",
    "name": "Inkwell",
    "summary": "A Mac writing workspace that keeps story notes, chapters, and scenes together.",
    "stack": [
      "Tauri",
      "React",
      "TypeScript",
      "Vite"
    ],
    "section": "building",
    "detail": {
      "eyebrow": "Desktop app",
      "lead": "A quiet place to organise a longer piece of writing and keep the next draft moving.",
      "about": [
        "Inkwell gives a writing project a structure: a story reference, chapters, and individual scenes. The reference holds details you need to keep consistent as the draft grows.",
        "Alongside the editor are a daily Submit Today routine, Markdown export, and a cover preview. The aim is to keep the writing and its supporting material close together."
      ],
      "features": [
        {
          "title": "Keep the story consistent",
          "text": "Collect characters, settings, and other reference notes alongside the draft."
        },
        {
          "title": "Work scene by scene",
          "text": "Organise a longer piece into chapters and scenes you can return to."
        },
        {
          "title": "Build a daily routine",
          "text": "Use Submit Today as a prompt to finish a piece of work for the day."
        },
        {
          "title": "Take the draft with you",
          "text": "Export the writing as Markdown and preview front and back cover artwork."
        }
      ],
      "focus": "Protecting the writing experience as a project grows, especially typing performance, finding text, and handling supporting media."
    }
  },
  {
    "slug": "reservist-strength",
    "name": "Reservist Strength",
    "summary": "Bring rosters, mission maps, routes, and timing reports into one planning workspace.",
    "stack": [
      "Next.js",
      "TypeScript",
      "Supabase",
      "Tailwind"
    ],
    "section": "building",
    "detail": {
      "eyebrow": "Internal tool",
      "lead": "A shared workspace for keeping people, routes, and mission details in view.",
      "about": [
        "Reservist Strength brings roster and reporting work together with a mission map. Objectives, positions, and routes can be reviewed alongside the information needed for planning.",
        "Part of the work focuses on phone use: adjusting a route by touch, moving around the map, and reading timing reports in the order of travel."
      ],
      "features": [
        {
          "title": "Plan on a map",
          "text": "Place mission objectives, prepared positions, and routes in a shared view."
        },
        {
          "title": "Adjust routes by touch",
          "text": "Move route bends on a phone while keeping map panning usable."
        },
        {
          "title": "Follow the travel order",
          "text": "Arrange dual-timing report entries to match the direction of the route."
        },
        {
          "title": "Keep team details together",
          "text": "Review rosters and mission lists, collect feedback, and generate reports."
        }
      ],
      "focus": "Making the map and reports agree. A route should read in the same order on screen and in the timing report."
    }
  },
  {
    "slug": "recyrcle",
    "name": "Recyrcle",
    "summary": "Track textile recycling from sorting to dispatch, and give partners a view of their batches and impact.",
    "stack": [
      "Next.js",
      "TypeScript",
      "Supabase",
      "Tailwind",
      "Recharts"
    ],
    "section": "client",
    "detail": {
      "eyebrow": "Client project",
      "lead": "Connect the day-to-day work of textile recycling with the records partners need to understand it.",
      "about": [
        "Recyrcle follows material through sorting, packing, and dispatch. Operations teams record that work through forms, with Indonesian language support where needed.",
        "Hotel and partner accounts have a separate view of batch history and impact reporting. Supporting files live in an asset library alongside the reporting workflow."
      ],
      "features": [
        {
          "title": "Follow material through operations",
          "text": "Record sorted material, rejected sacks, sealed bags, and dispatched loads."
        },
        {
          "title": "Give partners a clearer view",
          "text": "Show batch history and impact charts in dashboards for hotel and partner accounts."
        },
        {
          "title": "Keep reporting files together",
          "text": "Upload and organise supporting documents in a shared asset library."
        },
        {
          "title": "Match access to responsibility",
          "text": "Use account roles and permissions to control operational and administrative actions."
        }
      ],
      "focus": "Keeping operational records connected to the figures people see in a report, with room to explain how those figures are calculated."
    }
  },
  {
    "slug": "edthos",
    "name": "Edthos",
    "summary": "Manage course registrations, attendance, and invoices, with a connected portal for learners.",
    "stack": [
      "Next.js",
      "React",
      "TypeScript",
      "Supabase",
      "Tailwind"
    ],
    "section": "client",
    "detail": {
      "eyebrow": "Client project",
      "lead": "A connected workspace for the staff running training courses and the learners attending them.",
      "about": [
        "Edthos has two sides. Staff use a training management system to organise courses, registrations, attendance, and assessments. Learners use a portal to follow their enrolments and receive course information.",
        "The staff and learner views share the records they need, including notices, contact details, and registration fee invoices. The product supports multiple organisations."
      ],
      "features": [
        {
          "title": "Manage registrations",
          "text": "Track registration status, add remarks, and issue registration fee invoices."
        },
        {
          "title": "Run each course",
          "text": "Schedule course sessions and record attendance and assessments."
        },
        {
          "title": "Keep learners informed",
          "text": "Share notices, attachments, course details, and staff contact information."
        },
        {
          "title": "Support different organisations",
          "text": "Keep organisation contacts and administration connected to the relevant learner portal."
        }
      ],
      "focus": "Making the same course information useful to two different audiences: staff who maintain the records and learners who need to know what happens next."
    }
  },
  {
    "slug": "st-clare",
    "name": "St Clare",
    "summary": "Connect school records and individual learning plans with daily updates and a parent communication app.",
    "stack": [
      "React",
      "Expo",
      "TypeScript",
      "Supabase"
    ],
    "section": "client",
    "detail": {
      "eyebrow": "Client project",
      "lead": "A staff workspace and a parent app that connect classroom planning with everyday communication.",
      "about": [
        "St Clare combines a web workspace for school staff with a mobile app for parents. Staff manage student records, lesson plans, and individualised education programmes, or IEPs.",
        "Parents receive classroom updates and communicate through the app. Both sides use shared accounts, files, and supporting services."
      ],
      "features": [
        {
          "title": "Plan individual learning",
          "text": "Organise IEP goals, record progress, and generate PDF documents."
        },
        {
          "title": "Share the school day",
          "text": "Send classroom updates that parents can follow from the mobile app."
        },
        {
          "title": "Keep conversations connected",
          "text": "Exchange messages and media, search conversations, and return to quoted messages."
        },
        {
          "title": "Support staff and parents",
          "text": "Give staff tools suited to their roles and parents an app for iOS and Android."
        }
      ],
      "focus": "Making detailed school records manageable for staff while keeping the parent experience clear and easy to follow."
    }
  },
  {
    "slug": "loopnet",
    "name": "LoopNet",
    "summary": "Record scrap materials, explore indicative regional values, and find nearby recycling partners.",
    "stack": [
      "Next.js",
      "TypeScript",
      "Supabase",
      "Tailwind"
    ],
    "section": "client",
    "detail": {
      "eyebrow": "Client project",
      "lead": "Help operators understand what they have, what it might be worth, and who could take it next.",
      "about": [
        "LoopNet lets operators log scrap batches with photos and review their history. Regional value estimates and nearby partner suggestions help them explore possible next steps.",
        "A public estimate form gives new enquiries a way in. Optional AI image analysis can assist with reviewing scrap photos."
      ],
      "features": [
        {
          "title": "Record a batch",
          "text": "Save material details and photos, then return to the batch history."
        },
        {
          "title": "Explore indicative value",
          "text": "Use regional estimates as a starting point, rather than a confirmed sale price."
        },
        {
          "title": "Find nearby partners",
          "text": "Surface potential partners who could receive or process the material."
        },
        {
          "title": "Start with an estimate",
          "text": "Let prospective customers submit an enquiry through a public estimate form."
        }
      ],
      "focus": "Making estimates useful without suggesting more certainty than they provide. A value signal should help someone decide what to investigate next."
    }
  },
  {
    "slug": "awwa-scheduler",
    "name": "AWWA Scheduler",
    "summary": "Compare staff relief plans against staffing rules, then adjust assignments together.",
    "stack": [
      "Next.js",
      "TypeScript",
      "Supabase",
      "Tailwind"
    ],
    "section": "client",
    "detail": {
      "eyebrow": "Client project",
      "lead": "Help department heads cover staff absences while keeping established teams as stable as possible.",
      "about": [
        "AWWA Scheduler supports relief assignments across morning and afternoon sessions. It generates options against staffing requirements and scores how much each option disrupts existing arrangements.",
        "Department heads can compare those options, adjust assignments together, and export a plan. Temporary staff can be included in the scheduling work."
      ],
      "features": [
        {
          "title": "Check staffing requirements",
          "text": "Account for minimum staffing, certifications, and experience when generating options."
        },
        {
          "title": "Compare possible plans",
          "text": "Review disruption scores before choosing a relief schedule."
        },
        {
          "title": "Adjust assignments together",
          "text": "Make collaborative changes, apply manual overrides, and score the revised plan."
        },
        {
          "title": "Prepare the final schedule",
          "text": "Manage temporary staff assignments and export the plan for use."
        }
      ],
      "focus": "Helping people judge a plan, not just generating one. The rules and scores need to make trade-offs visible while leaving room for human judgement."
    }
  },
  {
    "slug": "local-rag",
    "name": "Local RAG Assistant",
    "summary": "Ask questions about files in selected folders, using a local search index and answers linked to sources.",
    "stack": [
      "Electron",
      "React",
      "Python",
      "ChromaDB",
      "OpenAI"
    ],
    "section": "client",
    "detail": {
      "eyebrow": "Client project",
      "lead": "Find an answer in your documents, then follow it back to the source.",
      "about": [
        "The Local RAG Assistant indexes selected folders so you can ask questions about their contents. Answers include citations that link back to the passages used.",
        "The search index is stored on the device. That describes where the index lives; privacy also depends on the model and services configured for answering questions."
      ],
      "features": [
        {
          "title": "Choose what to index",
          "text": "Select folders and build a searchable document index on the device."
        },
        {
          "title": "Check the source",
          "text": "Open cited passages to see the material behind an answer."
        },
        {
          "title": "Review folder changes",
          "text": "Notice new or changed files and decide when to update the index."
        },
        {
          "title": "Configure document search",
          "text": "Adjust how documents are split for indexing and choose the model used for answers."
        }
      ],
      "focus": "Making answers inspectable. Citations and clear indexing controls help a reader check what the assistant used, rather than taking an answer at face value."
    }
  }
]

export function getProjectBySlug(slug: string): PortfolioProject | undefined {
  return portfolioProjects.find(project => project.slug === slug)
}

export const buildingProjects = portfolioProjects.filter(project => project.section === 'building')
export const clientProjects = portfolioProjects.filter(project => project.section === 'client')
